import { Suspense, useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, createRoutesFromElements, Route } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { useAppEnvConfig, useAuthentication } from '@connect-x-app/hybrid-state';
import { getAccessToken } from '@connect-x-app/hybrid-utils';
import { queryClient } from '@connect-x-app/web-data-access';
import { About } from '@connect-x-app/web-feature-app-config';
import { useSetupEnvConfig } from '@connect-x-app/web-state';
import { Spinner, Toastify } from '@connect-x-app/web-ui';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { jwtDecode } from 'jwt-decode';
import { LDMultiKindContext, LDProvider } from 'launchdarkly-react-client-sdk';

import { App } from './App';
import { NavigationGuard } from './guards';
import { AxiosInterceptorProvider } from './interceptors';

import '@connect-x-app/shared-translations';

const FEATURE_FLAGS_ENABLED = false;

const PublicAboutComponent = () => {
  return (
    <>
      <About showLicenses={false} />
      <Toastify />
    </>
  );
};

const LaunchDarklyWrapper = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuthentication();
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [environment, setEnvironment] = useState<string | null>(null);

  useEffect(() => {
    const setCustomer = async () => {
      const token = await getAccessToken();
      if (token) {
        const decodedToken = jwtDecode<{ customer: string; environment: string }>(token);
        if (decodedToken?.customer) {
          setCustomerId(decodedToken.customer);
        }
        if (decodedToken?.environment) {
          setEnvironment(decodedToken.environment);
        }
      }
    };

    if (FEATURE_FLAGS_ENABLED) {
      setCustomer();
    }
  }, []);

  const context: LDMultiKindContext | null = useMemo(() => {
    if (!FEATURE_FLAGS_ENABLED || !user?.externalId || !customerId || !environment) {
      return null;
    }
    return {
      customer: {
        key: customerId,
      },
      environment: {
        key: environment,
      },
      kind: 'multi',
      user: {
        key: user.externalId,
      },
    };
  }, [customerId, environment, user?.externalId]);

  if (!FEATURE_FLAGS_ENABLED) {
    return children;
  }

  if (!context || !user?.featureFlagWebKey) {
    return children;
  }

  return (
    <LDProvider
      clientSideID={user?.featureFlagWebKey ?? ''}
      context={context}
      deferInitialization={!user || !customerId || !environment}
    >
      {children}
    </LDProvider>
  );
};

const RootComponent = () => {
  const { error, loading } = useSetupEnvConfig();
  const { env } = useAppEnvConfig();

  if (loading) {
    return <Spinner overlay />;
  }

  if (error ?? !env) {
    return <div>Error: {error}</div>;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <LaunchDarklyWrapper>
        <AxiosInterceptorProvider>
          <App />
          <NavigationGuard />
          <ReactQueryDevtools buttonPosition="bottom-left" initialIsOpen={false} />
        </AxiosInterceptorProvider>
      </LaunchDarklyWrapper>
    </QueryClientProvider>
  );
};

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route element={<RootComponent />} path="*" />
      <Route element={<PublicAboutComponent />} path="about" />
    </>,
  ),
);

createRoot(document.getElementById('root')!).render(
  <Suspense fallback={<Spinner overlay />}>
    <RouterProvider router={router} />
  </Suspense>,
);
